import React from 'react'
import styled from 'styled-components'
import { useConfirm } from 'sutra/components/confirm/use-confirm'
import { spacing } from 'sutra/tokens/spacing'

import type { FilterControlProps } from 'features/charts-v2'
import type { FilterOption } from 'features/filters/types'
// bully ignore[no-cross-feature-private-imports: StaleFiltersNotice is the canonical stale-filter surface shared with Issue Agent (PROD-5380); the same shape works for benchmarking's `stale_filters` payload — mirror the affordance rather than fork a second notice. permanent: colocated with the notice's owning feature.]
import {
  StaleFiltersNotice,
  type StaleFilterRemoval,
} from 'features/trend-detection/features/agent-create/components/StaleFiltersNotice/StaleFiltersNotice'
import type {
  AgentStaleFilter,
  FilterCriterion,
  StaleByField,
} from 'features/trend-detection/types'
import { ENTITY_LABELS } from 'src/components/agent-forms/benchmark-entity-labels'
import { BenchmarkEntityRadioGroup } from 'src/components/agent-forms/BenchmarkEntityRadioGroup'
import { BenchmarkUniversePanel } from 'src/components/agent-forms/BenchmarkUniversePanel'
import {
  FormField,
  Label,
  LabelGroup,
  StyledInput,
} from 'src/components/agent-forms/styles'
import { useBenchmarkCompareStats } from 'src/components/agent-forms/use-benchmark-compare-stats'
import { useFocusEntitySelection } from 'src/components/agent-forms/use-focus-entity-selection'

export type BenchmarkingEntityType = 'brand' | 'product_hierarchy'

export interface BenchmarkingAgentConfigFormProps {
  name: string
  onNameChange: (value: string) => void
  /** Whether the name field is editable. Defaults to true. */
  nameEditable?: boolean

  entityType: BenchmarkingEntityType | null
  onEntityTypeChange: (type: BenchmarkingEntityType) => void

  /** Prop bag for the shared Issue-Agent-style Filters section — see
   * `useAgentFilterPanel`. Universe (formerly "Comparison group") side. */
  filterControlProps: FilterControlProps

  /** Raw cohort (universe) criteria — needed alongside `filterControlProps`
   * for the chiclet labels and the live stats widget, both of which speak
   * `FilterCriterion[]`, not `FilterControlProps`. */
  cohortFilters: FilterCriterion[]
  focusGroupFilters: FilterCriterion[]
  onFocusGroupFiltersChange: (filters: FilterCriterion[]) => void

  /** Needed by the live stats widget's `compare-stats` call. */
  projectId: string | null

  /** Already scoped to `entityType` by the caller's own query (`fields:
   * [entityType]`) — see `useBenchmarkingSetup`. */
  focusGroupFilterOptions: FilterOption[] | undefined
  focusGroupFilterOptionsLoading: boolean

  /**
   * The server's staleness verdict, keyed by the field the CONFIG uses. The
   * picker asks per field; anything coarser offers one dimension's stale
   * value for removal on another. Universe-side stale highlighting has no
   * equivalent (see this form's own doc comment) — only the focus picker
   * still consumes this.
   */
  staleFocusByField?: StaleByField

  /**
   * Saved filter values the project's data no longer resolves — surfaces as
   * a yellow callout above the fields so the editor sees what to fix before
   * saving. Empty / undefined renders nothing. Covers both cohort and focus
   * scopes; the notice cross-references the union of both filter lists.
   */
  staleFilters?: readonly AgentStaleFilter[]
  /**
   * True when the current user can edit this agent. Optional so callers
   * that don't wire `staleFilters` don't need to touch it; when omitted
   * the notice renders read-only.
   */
  isEditor?: boolean
  /**
   * Fires when the user clicks Remove on a stale row. Callers typically
   * strip the value from whichever of `cohortFilters` / `focusGroupFilters`
   * carries it. Omitted → no Remove buttons render (viewer surfaces).
   */
  onRemoveStaleValue?: (removal: StaleFilterRemoval) => void
  /**
   * True while the stale-filter compute is in flight — renders a subtle
   * placeholder in the notice slot so the modal doesn't feel silent.
   * The compute takes ~1-3s (filter-options fetch + theme resolution).
   */
  isStaleFiltersChecking?: boolean
}

/**
 * Shared edit-form for benchmarking agent fields. Consumed by:
 *   - BenchmarkingAgentFields (features/trend-detection/features/agent-create
 *     — the customer create form, nameEditable=false)
 *   - BenchmarkingAgentConfigPanel (customer edit panel — wraps this in edit mode only)
 *   - BenchmarkingAgentEditor (Yogi-staff settings modal)
 *
 * Renders the editable fields only; each wrapper supplies its own surrounding
 * chrome (edit-mode toggle, modal action buttons).
 *
 * Switching entity type while focus-group filters are populated prompts the
 * user to confirm — the actual clearing of `focusGroupFilters` happens in the
 * caller's `onEntityTypeChange` (see `useBenchmarkingSetup.setEntityType`).
 *
 * Owns the one `useBenchmarkCompareStats` call for this surface — the
 * Universe and Focus summary rows both read off its single response rather
 * than each running their own debounced query, which would let their query
 * keys diverge transiently and double the requests.
 */
export const BenchmarkingAgentConfigForm: React.FC<
  BenchmarkingAgentConfigFormProps
> = ({
  name,
  onNameChange,
  nameEditable = true,
  entityType,
  onEntityTypeChange,
  filterControlProps,
  cohortFilters,
  focusGroupFilters,
  onFocusGroupFiltersChange,
  projectId,
  focusGroupFilterOptions,
  focusGroupFilterOptionsLoading,
  staleFocusByField,
  staleFilters,
  isEditor = false,
  onRemoveStaleValue,
  isStaleFiltersChecking = false,
}) => {
  const confirm = useConfirm()

  const handleEntityTypeChange = (newType: BenchmarkingEntityType): void => {
    if (
      entityType !== null &&
      entityType !== newType &&
      focusGroupFilters.length > 0
    ) {
      const newLabel = ENTITY_LABELS[newType].section
      const currentLabel = ENTITY_LABELS[entityType].singular
      void confirm({
        title: `Switch to ${newLabel}?`,
        content: `Your current ${currentLabel} selections will be cleared.`,
        okText: `Switch to ${newLabel}`,
        cancelText: 'Cancel',
        onOk: () => onEntityTypeChange(newType),
      })
    } else {
      onEntityTypeChange(newType)
    }
  }

  const focus = useFocusEntitySelection({
    criteria: focusGroupFilters,
    onCriteriaChange: onFocusGroupFiltersChange,
    entityField: entityType,
    filterOptions: focusGroupFilterOptions,
    filterOptionsLoading: focusGroupFilterOptionsLoading,
    staleByField: staleFocusByField,
  })

  const {
    stats,
    isLoading: statsLoading,
    isUnavailable: statsUnavailable,
  } = useBenchmarkCompareStats({
    projectId,
    cohortFilters,
    focusGroupFilters,
  })

  const universeSectionCaption = entityType
    ? `What ${ENTITY_LABELS[entityType].section.toLowerCase()} should we benchmark?`
    : 'What brands or products should we benchmark?'

  // Cross-reference the notice against the union of both scopes' criteria so
  // a value the user has already removed disappears without a refetch.
  const allCriteria = [...cohortFilters, ...focusGroupFilters]

  return (
    <FormFields>
      {nameEditable && (
        <FormField>
          <LabelGroup>
            <Label>Name</Label>
          </LabelGroup>
          <StyledInput
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Premium Cookies"
          />
        </FormField>
      )}

      <BenchmarkEntityRadioGroup
        value={entityType}
        onChange={handleEntityTypeChange}
      />

      <UniverseSection>
        <Label>{universeSectionCaption}</Label>
        <BenchmarkUniversePanel
          entityType={entityType}
          universeFilterControlProps={filterControlProps}
          universeFilters={cohortFilters}
          focus={focus}
          universeStats={stats?.comparison ?? null}
          focusStats={stats?.focus ?? null}
          statsLoading={statsLoading}
          statsUnavailable={statsUnavailable}
          dateWindowLabel={stats?.date_window_label ?? null}
        />
      </UniverseSection>
      {isStaleFiltersChecking && !staleFilters && (
        <StaleFiltersCheckingPlaceholder>
          Checking filter health…
        </StaleFiltersCheckingPlaceholder>
      )}
      {staleFilters && staleFilters.length > 0 && (
        <StaleFiltersNotice
          staleFilters={staleFilters}
          criteria={allCriteria}
          isEditor={isEditor}
          onRemove={onRemoveStaleValue}
        />
      )}
    </FormFields>
  )
}

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]}px;
`

const UniverseSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]}px;
`

// Muted neutral (not amber) so it reads as "still checking" rather than
// "we found a problem." Same slot the notice occupies once the check
// resolves, so there's no layout shift.
const StaleFiltersCheckingPlaceholder = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: #666;
  font-style: italic;
`
